import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import { createFacebookShareUrl, createTwitterShareUrl, getUserUpdateLink } from '@gik/core/utils/ShareUtils';
import {
  AnimatedDivBone,
  Button,
  IButtonProps,
  ILightboxImageMediaType,
  ILightboxVideoMediaType,
  LayoutAside,
  LightboxMedia,
  LightboxMediaType,
  LineClamp,
  LinkParser,
  LoadingSpinner,
  Separator,
  SvgIcon,
  UI,
} from '@gik/ui';
import moment from 'moment';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { copyWithNotification } from '@gik/core/utils/LinkUtils';
import { dateDisplayFormatFull, monthAndDayDisplayFormat, weekDayDisplayFormat, yearDisplayFormat } from '@gik/l10n';
import { removeUpdate } from '@gik/inkind-page/api/UserUpdatesAPI';
import { IInkindUserUpdatesTimelineModalProps } from '@gik/inkind-page/components/InkindUserUpdatesTimelineModal/InkindUserUpdatesTimelineModal';
import InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import commonTranslationKeys from '@gik/i18n/en/common';

export interface IInkindUserUpdateTimelineCardProps
  extends Pick<IInkindUserUpdatesTimelineModalProps, 'onDeleteUpdate'> {
  update: InkindUserUpdate;
  isOrganizer: boolean;

  openEditUpdateModal(update: InkindUserUpdate): void;
}

const blockName = 'inkind-user-update-timeline-card';

export default function InkindUserUpdateTimelineCard({
  update,
  isOrganizer,
  openEditUpdateModal = noop,
  onDeleteUpdate = noop,
}: IInkindUserUpdateTimelineCardProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();
  const [deleting, setDeleting] = React.useState<boolean>(false);

  const updateLink = React.useMemo(() => update && encodeURIComponent(getUserUpdateLink(update)), [update]);

  const baseShareButtonsOptions: Partial<IButtonProps> = {
    variant: 'default-plain',
    circle: true,
  };

  async function openDeleteConfirmationModal() {
    // TODO i18n
    if (
      await UI.confirm(t(translationKeys.InkindUserUpdateTimelineCardDeleteConfirmCopy), {
        okText: t(commonTranslationKeys.common.delete),
      })
    ) {
      setDeleting(true);
      await removeUpdate(update.id);
      onDeleteUpdate();
    }
  }

  function openAttachmentLightbox(index: number) {
    const media: LightboxMediaType[] = update.media.map(value => {
      switch (value.type) {
        case 'photo':
          return {
            mediaType: LightboxMedia.IMAGE,
            url: value.url,
            caption: value.caption,
          } as ILightboxImageMediaType;
        case 'video':
          return {
            mediaType: LightboxMedia.VIDEO,
            url: value.url,
            caption: value.caption,
          } as ILightboxVideoMediaType;
      }
    });

    UI.lightbox(media, {}, index);
  }

  return (
    <LayoutAside {...bem(null, [{ deleting }])} id={`timeline-card-${update?.id}`}>
      {deleting && <LoadingSpinner {...bem('deleting-spinner')} />}
      {!deleting && (
        <>
          <main>
            <LineClamp lines={2} showButton={false} fadeOut={false} {...bem('title')} tooltip>
              {update.title}
            </LineClamp>

            <p {...bem('updated-by')}>Updated by {update.organiserFullName}</p>
            <div {...bem('share-buttons')}>
              <Button
                {...baseShareButtonsOptions}
                type={'button'}
                tooltip="Copy link"
                onClick={() =>
                  copyWithNotification(
                    decodeURIComponent(updateLink),
                    t(translationKeys.InkindUserUpdateTimelineCardLinkCopiedNotification)
                  )
                }
              >
                <SvgIcon name={'Link'} />
              </Button>

              <Button
                {...baseShareButtonsOptions}
                externalLink
                href={createFacebookShareUrl(updateLink)}
                tooltip={t(translationKeys.InkindUserUpdateTimelineCardShareFacebook)}
              >
                <SvgIcon set={'brand-icons'} name={'Facebook'} />
              </Button>

              <Button
                {...baseShareButtonsOptions}
                externalLink
                href={createTwitterShareUrl(updateLink)}
                tooltip={t(translationKeys.InkindUserUpdateTimelineCardShareTwitter)}
              >
                <SvgIcon set={'brand-icons'} name={'Twitter'} />
              </Button>
            </div>

            <LineClamp lines={10}>
              <LinkParser>{update.description}</LinkParser>
            </LineClamp>

            {update.media.length > 0 && (
              <>
                <p {...bem('attachments-label')}>{t(translationKeys.InkindUserUpdateTimelineCardAttachmentLabel)}</p>

                <Separator size={'xs'} type={'dashed'} />

                <div {...bem('attachments-wrapper')}>
                  {update.media.map((media, index) => (
                    <div
                      key={media.id}
                      {...bem('attachment-wrapper', [{ [media.type]: true }])}
                      onClick={() => openAttachmentLightbox(index)}
                    >
                      <AnimatedDivBone {...bem('thumbnail-skeleton')} />
                      <img alt="Thumbnail" src={media.thumbnailUrl} {...bem('attachment')} />
                      {media.type === 'video' && (
                        <div {...bem('play-icon-wrapper')}>
                          <SvgIcon name={'Play'} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </main>
          <aside {...bem('aside')}>
            <Separator {...bem('line')} type={'solid'} thickness={'normal'} direction={'vertical'} />
            <div {...bem('aside-content')}>
              <div
                {...bem('date')}
                title={moment(update.publishedDate, DateTimeUtils.DateFormat).format(dateDisplayFormatFull)}
              >
                <p {...bem('week-day')}>
                  {moment(update.publishedDate, DateTimeUtils.DateFormat).format(weekDayDisplayFormat).toUpperCase()}
                </p>
                <p {...bem('month-and-day')}>
                  {moment(update.publishedDate, DateTimeUtils.DateFormat).format(monthAndDayDisplayFormat)}
                </p>
                <p {...bem('year')}>
                  {moment(update.publishedDate, DateTimeUtils.DateFormat).format(yearDisplayFormat)}
                </p>
              </div>
              {isOrganizer && (
                <Button
                  circle
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => openEditUpdateModal(update)}
                  tooltip={t(translationKeys.EditUpdateButtonLabel)}
                >
                  <SvgIcon name={'PencilOutline'} />
                </Button>
              )}
              {isOrganizer && (
                <Button
                  circle
                  size={'sm'}
                  variant={'danger'}
                  onClick={openDeleteConfirmationModal}
                  tooltip={t(translationKeys.DeleteUpdateButtonLabel)}
                >
                  <SvgIcon name={'X'} />
                </Button>
              )}
            </div>
          </aside>
        </>
      )}
    </LayoutAside>
  );
}
